import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { resolveImageUrl } from "../lib/images";

/**
 * Generic creations gallery, used for both Number Cake & Cookies perso.
 * Props:
 *   endpoint  : e.g. "/numbercake-gallery" or "/cookie-perso-gallery"
 *   tagText   : section-tag handwritten text (e.g. "nos réalisations")
 *   title     : main title
 *   subtitle  : descriptive paragraph
 *   testid    : data-testid prefix (e.g. "numbercake-gallery", "perso-gallery")
 */
export default function CreationsGallery({ endpoint, tagText, title, subtitle, testid }) {
    const [items, setItems] = useState([]);

    const fetchGallery = useCallback(async () => {
        try {
            const { data } = await api.get(endpoint);
            setItems(data || []);
        } catch (_e) {
            // ignore
        }
    }, [endpoint]);

    useEffect(() => {
        fetchGallery();
    }, [fetchGallery]);

    if (items.length === 0) return null;

    return (
        <section className="mt-20 pt-12 border-t-2 border-[#E4D1BB]" data-testid={testid}>
            <div className="text-center mb-10">
                <p className="section-tag">{tagText}</p>
                <h3 className="font-display text-4xl md:text-5xl text-[#2A1408] leading-[0.9]">
                    {title}
                </h3>
                <p className="mt-3 text-[#5C3D2E] max-w-2xl mx-auto">{subtitle}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((it, i) => {
                    const angle = (i % 4) - 1.5;
                    return (
                        <figure
                            key={it.id}
                            className="overflow-hidden rounded-xl border-2 border-[#2A1408] bg-[#FFF1E0] transition-transform duration-300 hover:!rotate-0"
                            style={{
                                transform: `rotate(${angle * 0.8}deg)`,
                                boxShadow: "4px 4px 0 #2A1408",
                            }}
                            data-testid={`${testid}-item-${it.id}`}
                        >
                            <div className="aspect-square overflow-hidden">
                                <img
                                    src={resolveImageUrl(it.image_url)}
                                    alt={it.caption || "Création"}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            {it.caption && (
                                <figcaption className="px-3 py-2 text-xs text-[#3D1F0F] text-center font-handwritten text-base">
                                    {it.caption}
                                </figcaption>
                            )}
                        </figure>
                    );
                })}
            </div>
        </section>
    );
}
